export const blackMFC = 'rgb(67, 67, 67)'

export const greenMFC = 'rgb(56, 142, 60)'

export const activeGreen = 'green'

export const CAPTION_HEIGHT_RATIO = 0.18

export const COMPOSE_ICONS_SPACING = 1
export const PADDING_GRID_BLEED = 4 // 2

export const ROOM_BORDER_WIDTH = 1

export const CHAT_HEAD_WIDTH = 48

export const APP_FONT = `'Montserrat', 'Helvetica', 'Arial', sans-serif`

export const FONT_SIZES = {
    body1: '0.9285714285714286rem',
    body2: '0.8125rem',
    body3: '0.74rem',
    caption: '0.6964285714285714rem',
    h1: '5.571428571428571rem',
    h2: '3.482142857142857rem',
    h3: '2.7857142857142856rem',
    h4: '1.9732142857142858rem',
    h5: '1.3928571428571428rem',
    h6: '1.1607142857142858rem',
    subtitle1: '0.9285714285714286rem',
    subtitle2: '0.8125rem',
    overline: '0.6964285714285714rem',
}

/**
 * Commonly found zIndex values across app.mfc
 */
export const PAPER_HEIGHTS = {
    // sorted by z-index elevation
    // max z-index is a signed int32: 2147483647
    highest: /*             */ 2147483647,
    popover: /*             */ 2147483610,
    tooltip: /*             */ 2147483603,
    tip: /*                 */ 2147483602,
    videoForefront: /*      */ 2147483601,
    snapStream: /*          */ 2147483600,
    dialogConfirmation: /*  */ 999999999 + 3,
    // full screen over the nav footer and the sidebar handle; it steps
    // under the menus and drawers it opens (videoAvatarViewerUnderModals)
    videoAvatarViewer: /*   */ 1000000001,
    footer: /*              */ '999999999 !important' as any,
    dragSidebar: /*         */ 1000000000,
    dialog: /*              */ 999999999,
    drawer: /*              */ 999999995,
    quickReply: /*          */ 999999994,
    // the video avatar viewer while a menu or drawer is open over it
    videoAvatarViewerUnderModals: 999999993,
    // floatingPreview: /*     */ 999999991,
    floater: /*             */ 999999990,
    emoticon: /*            */ 99999999,
    performance: /*         */ 9999999,
    videoPreview: /*        */ 9999999,
    videoBlackout: /*       */ 9999998,
    closeButton: /*         */ 1301,
    // the hosted SDK control bar in mobile theater: its settings popup
    // opens over the chat pane, so it sits one step above floatingChat
    floatingControls: /*    */ 1301,
    floatingActionBar: /*   */ 1300,
    floatingChat: /*        */ 1300,
    roomCover: /*           */ 1299,
    videoStreamContainer: /**/ 1298,
    fullWindowVideo: /*     */ 1000,
    midLayer: /*            */ 24,
    base: /*                */ 1,
    zero: /*                */ 0,
    low: /*                 */ -1,
} as const

export const COMPONENT_HEIGHTS = {
    actionsBar: 40,
    availableSpaceControlBar: 42,
    availableSpaceMinimum: 80,
    chatArchivesHeader: 48,
    chatHeader: 48,
    chatHeaderOffline: 70,
    chatHeaderOfflineDesktop: 96,
    compose: 40,
    detailsTileHeader: 52,
    detailsTileHeadline: 23,
    detailsTileTopic: 68,
    detailsTileInfo: 26,
    drawerTitle: 48,
    headerCompact: 24,
    headerDesktop: 40,
    // matches a bordered Select with a label — they sit side by side in toolbars
    inputSearch: 36,
    nav: 62,
    navBottom: 55,
    navExpanded: 52,
    navSmall: 50,
    shareSliderDefault: 160,
    tabs: 48,
    whiteboardTitle: 36,
    whiteboardTip: 90,
} as const

export const COMPONENT_WIDTHS = {
    drawer: 300,
    leftNav: 56,
    leftNavExpanded: 226,
    sidebar: 180,
    dragSidebar: 64,
    tagsPanel: 200,
    tagsPanelSmall: 160,
    detailsTile: 330,
    // a player control button: the SDK's 4em --mfcvs-button-width at its 10px base font
    videoControl: 40,
    // the expand-lower-panel button's lane in the available-space toolbar
    availableSpaceExpandButton: 40,
} as const

export const HEIGHT_OFFSET =
    COMPONENT_HEIGHTS.chatHeader +
    COMPONENT_HEIGHTS.actionsBar +
    COMPONENT_HEIGHTS.compose

export const MIN_CHAT_HEIGHT = 180
export const MIN_VIDEO_WIDTH = 320
export const MIN_CHAT_WIDTH = 250

const getComputedPropertyValue = (property: string) => () =>
    typeof window !== 'undefined'
        ? Number(
              window
                  .getComputedStyle(document.documentElement)
                  .getPropertyValue(property) // unfortunately querying the env var directly isnt reliable across browsers
                  .split('px')[0],
          ) || 0
        : 0

// JS access to `safe-area-inset-bottom` and `safe-area-inset-left` values -
// this is the extra space that mobile browsers add to make space for camera
// or speaker grills interrupting the screen, so it has to be accounted for in
// modals and anything else that needs to have an accurate height from bottom
export const getBrowserBottomInset = getComputedPropertyValue('--saib')

export const EXPANDED_MOBILE_SEARCH_HEIGHT = COMPONENT_HEIGHTS.inputSearch + 8

export const MIN_CONTENT_WIDTH_MOBILE = 360
export const MIN_CONTENT_WIDTH_DESKTOP = 480

export const PINNED_ROOM_CHAT_MIN_WIDTH = 250
export const PINNED_ROOM_MAX_HEIGHT = 650
export const PINNED_ROOM_MIN_WIDTH_FOR_CHAT = 540

// USE THE `no-scrollbars` CSS CLASS INSTEAD
// className="no-scrollbars"
// export const NO_SCROLLBAR_STYLES = {
//     overflowX: 'hidden',
//     scrollbarWidth: 'none',
//     scrollbarColor: 'transparent transparent',
//     '&::WebkitScrollbar': {
//         width: 0,
//         height: 0,
//     },
//     '&::WebkitScrollbarTrack': {
//         background: 'transparent',
//     },
//     '&::WebkitScrollbarThumb': {
//         backgroundColor: 'transparent',
//     },
//     '&::-webkit-scrollbar': {
//         width: 0,
//         height: 0,
//     },
//     '&::-webkit-scrollbar-track': {
//         background: 'transparent',
//     },
//     '&::-webkit-scrollbar-thumb': {
//         backgroundColor: 'transparent',
//     },
// } as const
